import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Formik } from 'formik';
import * as Yup from 'yup';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import ButtonsContainer from '~/components/core/Atomic/Buttons/ButtonsContainer';
import CancelButton from '~/components/core/Buttons/CancelButton';
import TextFieldFormik from '~/components/TextFieldFormik';

import CardDialog from '../CardDialog';
import { Heading } from '../core';
import { WarningIcon } from '../icons';

import type { GuardrailViolation } from './types';
import { isBlockingOutcome } from './types';

const OVERRIDE_REASON_FIELD = 'override_reason';
// Cap a single round's reason so the accumulated X-Guardrail-Override header (merged
// reasons + acknowledged keys across replay rounds) can't grow past typical server
// header limits (~8-16KB).
const MAX_OVERRIDE_REASON_LENGTH = 1000;

interface GuardrailsViolationDialogProps {
  violations: GuardrailViolation[];
  isBlocked: boolean;
  onClose: () => void;
  onProceed: (reason: string) => void | Promise<void>;
}

const GuardrailsViolationDialog: React.FC<GuardrailsViolationDialogProps> = ({
  violations,
  isBlocked,
  onClose,
  onProceed,
}) => {
  const theme = useTheme();

  return (
    <Formik
      initialValues={{ [OVERRIDE_REASON_FIELD]: '' }}
      validationSchema={Yup.object().shape({
        [OVERRIDE_REASON_FIELD]: isBlocked ? Yup.string() : Yup.string().trim().required('Required'),
      })}
      onSubmit={async (values) => {
        await onProceed(values[OVERRIDE_REASON_FIELD]);
      }}
    >
      {(formikProps) => (
        <CardDialog noCardTitle isDialog onClose={onClose} maxWidth="sm" fullWidth>
          <div className="text-center">
            <WarningIcon iconColor={theme.palette.warning.main} size={50} />
            <Heading variant="h1">
              {isBlocked
                ? `This action is blocked`
                : `Please review potential issue${violations.length > 1 ? 's' : ''}`}
            </Heading>
          </div>

          <div className="max-h-[450px] overflow-auto">
            {violations.map((violation) => (
              <AlertBanner
                key={violation.key}
                className="my-12"
                withIcon
                alertType={
                  isBlockingOutcome(violation.type) ? AlertBanner.ALERT_TYPES.ERROR : AlertBanner.ALERT_TYPES.WARNING
                }
                title={isBlockingOutcome(violation.type) ? 'Blocked' : 'Warning'}
                note={violation.message}
              />
            ))}
          </div>

          {!isBlocked && (
            <div>
              <TextFieldFormik
                className="my-12"
                id={OVERRIDE_REASON_FIELD}
                label="Override reason"
                fullWidth
                inputProps={{ maxLength: MAX_OVERRIDE_REASON_LENGTH }}
              />
            </div>
          )}

          <ButtonsContainer>
            <CancelButton disabled={formikProps.isSubmitting} onClick={onClose} />
            {!isBlocked && (
              <Button
                variant="contained"
                color="primary"
                disabled={formikProps.isSubmitting}
                onClick={() => formikProps.handleSubmit()}
              >
                Proceed
              </Button>
            )}
          </ButtonsContainer>
        </CardDialog>
      )}
    </Formik>
  );
};

export default GuardrailsViolationDialog;
